import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Home, Award, Calendar, TrendingUp, Users, Globe } from "lucide-react";
import Since1998Badge from "@/components/Since1998Badge";

const OurHistory = () => {
  const navigate = useNavigate();

  const timeline = [
    { year: 1995, event: "Domain upost.com registered (one of ours)", icon: Globe },
    { year: 1998, event: "its-sold.com goes live → First FSBO site in Texas", icon: Home, highlight: true },
    { year: 2004, event: "Facebook launches", icon: Users },
    { year: 2005, event: "YouTube launches", icon: TrendingUp },
    { year: 2006, event: "Zillow launches", icon: Home },
    { year: "2010-2024", event: "Domain held in reserve, waiting for the perfect market", icon: Calendar },
    { year: 2025, event: "its-sold.com relaunches statewide with zero-commission FSBO tools", icon: Award, highlight: true }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-slate-50">
      {/* Navigation */}
      <nav className="bg-white border-b border-orange-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-2 cursor-pointer" onClick={() => navigate('/')}>
              <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center">
                <Home className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Playfair Display' }}>
                Its-Sold.com<sup className="text-xs">™</sup>
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Since1998Badge variant="header" />
              <Button onClick={() => navigate('/')} variant="ghost">
                Home
              </Button>
              <Button onClick={() => navigate('/register')} className="bg-orange-600 hover:bg-orange-700">
                List Your Home
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <div className="pt-20 pb-12 px-4">
        <div className="max-w-4xl mx-auto text-center">
          <Since1998Badge variant="hero" />
          <h1 className="text-5xl font-bold text-gray-900 mt-8 mb-4" style={{ fontFamily: 'Playfair Display' }}>
            The 27-Year History of Its-Sold.com
          </h1>
          <p className="text-2xl text-gray-700 mb-4">
            America's Original FSBO Marketplace Since 1998
          </p>
        </div>
      </div>

      {/* Story Section */}
      <div className="max-w-4xl mx-auto px-4 pb-12">
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <p className="text-lg text-gray-700 leading-relaxed mb-6">
            In <strong>May 1998</strong>, when only 2 million websites existed on the entire internet, 
            <strong className="text-orange-600"> its-sold.com</strong> launched with a simple idea:
          </p>
          <p className="text-2xl font-bold text-center text-orange-600 my-8 py-6 bg-orange-50 rounded-lg">
            &ldquo;Homeowners should be able to sell their own houses and keep the commission.&rdquo;
          </p>
          <p className="text-lg text-gray-700 leading-relaxed mb-4">
            Amazon was still just books. Google was brand new. Facebook, YouTube, and Zillow didn&apos;t exist yet.
          </p>
          <p className="text-lg text-gray-700 leading-relaxed">
            But <strong>its-sold.com was already here</strong>, helping regular people sell homes without agents.
          </p>
        </div>

        {/* Timeline */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-12">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" style={{ fontFamily: 'Playfair Display' }}>
            Our Journey
          </h2>
          <div className="space-y-6">
            {timeline.map((item, index) => {
              const Icon = item.icon;
              return (
                <div 
                  key={index}
                  className={`flex items-start space-x-4 p-4 rounded-lg transition-all ${
                    item.highlight 
                      ? 'bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-500' 
                      : 'bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center ${
                    item.highlight ? 'bg-orange-600' : 'bg-gray-600'
                  }`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="flex-1">
                    <div className={`text-xl font-bold mb-1 ${
                      item.highlight ? 'text-orange-600' : 'text-gray-900'
                    }`}>
                      {item.year}
                    </div>
                    <div className="text-gray-700">{item.event}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Closing Message */}
        <div className="bg-gradient-to-r from-orange-600 to-red-600 rounded-2xl shadow-xl p-12 text-center text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
            27 Years Later
          </h2>
          <p className="text-xl mb-4">The mission is the same:</p>
          <p className="text-3xl font-bold mb-6">Put More Money in the Seller&apos;s Pocket.</p>
          <p className="text-lg mb-8">
            Welcome to the new its-sold.com — same name, same values, completely rebuilt for 2025.
          </p>
          <div className="space-y-4">
            <p className="text-2xl font-bold">
              Free site listings. Optional MLS upgrade. No 6% commission. Ever.
            </p>
            <Button 
              onClick={() => navigate('/register')}
              size="lg"
              className="bg-white text-orange-600 hover:bg-gray-100 font-bold text-xl px-12 py-6"
            >
              List Your Home Free Today →
            </Button>
          </div>
        </div>

        {/* Fun Facts */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-4xl font-bold text-orange-600 mb-2">27</div>
            <div className="text-sm text-gray-600">Years of Experience</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-4xl font-bold text-orange-600 mb-2">1998</div>
            <div className="text-sm text-gray-600">Year Established</div>
          </div>
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            <div className="text-4xl font-bold text-orange-600 mb-2">$0</div>
            <div className="text-sm text-gray-600">Commission. Ever.</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OurHistory;
